import { validateFieldValue } from "../../../utils/extensionFieldUtils";
import { ExtensionFieldCellEditor, CellEditorParams } from "./types";
import {
  CELL_EDITOR_STYLES,
  handleCellEditorKeyboard,
  handleNumericPaste,
  isAllowedKey,
  updateValidationState,
} from "./utils/cellEditorStyles";

/**
 * Currency Cell Editor for currency_usd extension fields
 * Provides currency-specific formatting and validation
 * Simplified to use DOM manipulation instead of React rendering
 */
export class CurrencyCellEditor implements ExtensionFieldCellEditor {
  private eGui: HTMLElement;
  private params!: CellEditorParams;
  private inputElement: HTMLInputElement;
  private currentValue: string;
  private initialValue: string;

  constructor() {
    this.eGui = document.createElement("div");
    Object.assign(this.eGui.style, CELL_EDITOR_STYLES.container);
    this.currentValue = "";
    this.initialValue = "";

    this.inputElement = document.createElement("input");
    this.setupInputElement();
  }

  private setupInputElement(): void {
    this.inputElement.name = "cellValue";
    this.inputElement.placeholder = "$0.00";
    Object.assign(this.inputElement.style, CELL_EDITOR_STYLES.input);

    // Add event listeners directly (AG Grid handles optimization)
    this.inputElement.addEventListener("input", this.handleInputChange);
    this.inputElement.addEventListener("focus", this.handleFocus);
    this.inputElement.addEventListener("blur", this.handleBlur);
    this.inputElement.addEventListener("keydown", this.handleKeyDown);
    this.inputElement.addEventListener("paste", this.handlePaste);

    this.eGui.appendChild(this.inputElement);
  }

  init(params: CellEditorParams): void {
    this.params = params;

    // Extract initial value from the extension field value object
    const extensionFieldValue = params.value as {
      floatValue?: number | null;
    } | null;

    let initialFloatValue: number | null = null;
    if (extensionFieldValue && typeof extensionFieldValue === "object") {
      initialFloatValue = extensionFieldValue.floatValue ?? null;
    }

    this.initialValue =
      initialFloatValue !== null && initialFloatValue !== undefined
        ? String(initialFloatValue)
        : "";
    this.currentValue = this.initialValue;
    this.inputElement.value = this.currentValue;
  }

  private formatCurrency(value: string): string {
    if (!value) return "";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return numValue.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  private parseCurrencyInput(value: string): string {
    // Remove currency symbols, commas, and whitespace for internal storage
    return value.replace(/[$,\s]/g, "").trim();
  }

  private handleInputChange = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    const cleanValue = this.parseCurrencyInput(newValue);

    this.currentValue = cleanValue;
    this.updateValidationState(cleanValue);
  };

  private updateValidationState(value: string): void {
    const validationError = validateFieldValue(value, this.params.fieldType);
    updateValidationState(this.inputElement, validationError);
  }

  private handleFocus = (): void => {
    // Show raw number when focused for easier editing
    this.inputElement.value = this.currentValue;
  };

  private handleBlur = (): void => {
    // Format as currency when not focused
    if (this.currentValue && !this.inputElement.title) {
      this.inputElement.value = this.formatCurrency(this.currentValue);
    }
    this.params.api.stopEditing();
  };

  private handleKeyDown = (event: KeyboardEvent): void => {
    // Handle currency-specific validation first
    const isNumberKey = /^[0-9]$/.test(event.key);
    const isDecimalPoint = event.key === "." && !this.inputElement.value.includes(".");

    if (!isAllowedKey(event) && !isNumberKey && !isDecimalPoint) {
      event.preventDefault();
      return;
    }

    // Use shared keyboard handler for Enter/Escape behavior
    handleCellEditorKeyboard(event, {
      inputElement: this.inputElement,
      initialValue: this.initialValue,
      currentValueSetter: (value) => {
        this.currentValue = value;
      },
      api: this.params.api,
    });
  };

  private handlePaste = (event: ClipboardEvent): void => {
    handleNumericPaste(event, this.inputElement, /[^0-9.-]/g, (value) => {
      this.currentValue = value;
    });
  };

  afterGuiAttached(): void {
    if (this.inputElement) {
      this.inputElement.focus();
      this.inputElement.select();
    }
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  getValue(): string {
    return this.currentValue || "";
  }

  destroy(): void {
    // AG Grid handles cleanup automatically, minimal cleanup needed
  }

  isPopup(): boolean {
    return false;
  }

  isCancelBeforeStart(): boolean {
    return false;
  }

  isCancelAfterEnd(): boolean {
    return false;
  }

  focusIn(): void {
    if (this.inputElement) {
      this.inputElement.focus();
    }
  }

  focusOut(): void {
    // No specific action needed on focus out
  }
}
